import { COLOMBIAN_DEPARTMENTS } from './constants'
import { LocaleConfig } from '@/config/types'
import { ColombianAddress } from '@/config/co/types'
import { Country } from '@/i18n/routing'
import { safeStringTrim } from '@/utils/safe-trim'

export const coConfig: LocaleConfig = {
  name: 'Colombia',
  country: 'CO',
  coordinates: {
    lat: 4.711,
    lng: -74.0721
  },
  timezone: 'America/Bogota',
  phoneFormat: '+57 XXX XXX XXXX',
  dateFormat: 'dd/MM/yyyy',
  flag: '/colombia.png',
  mainPoliticalDivisions: COLOMBIAN_DEPARTMENTS,
  zoomLevels: {
    country: 6,
    veryLarge: 7,
    large: 9,
    medium: 11,
    small: 13,
    verySmall: 15,
    default: 6
  },
  radiusConfig: {
    country: 50000, // Smaller radius for Colombia
    administrativeArea: 25000,
    locality: 8000,
    neighborhood: 2000,
    route: 1000,
    intersection: 200,
    premise: 100,
    pointOfInterest: 500
  },
  addressConfig: {
    mainPoliticalDivisionField: 'department',
    country: Country.CO,
    formatter: (data: ColombianAddress) => {
      const parts: string[] = []

      // Build the street/route part
      let streetPart = ''

      const addressType = safeStringTrim(data.addressType)
      if (addressType) {
        streetPart = addressType

        const primaryStreetNumber = safeStringTrim(data.primaryStreetNumber)
        if (primaryStreetNumber) {
          streetPart += ` ${primaryStreetNumber}`

          if (data.bis) {
            streetPart += ' bis'
          }

          const secondaryNumber = safeStringTrim(data.secondaryNumber)
          if (secondaryNumber) {
            streetPart += ` - ${secondaryNumber}`
          }

          const orientation = safeStringTrim(data.orientation)
          if (orientation) {
            streetPart += ` ${orientation.toLowerCase()}`
          }
        }

        parts.push(streetPart)
      }

      // Additional info (apartment, floor, etc.)
      const additionalInfo = safeStringTrim(data.additionalInfo)
      if (additionalInfo) {
        parts.push(additionalInfo)
      }

      // Neighborhood (barrio)
      const neighborhood = safeStringTrim(data.neighborhood)
      if (neighborhood) {
        parts.push(neighborhood)
      }

      // City
      const city = safeStringTrim(data.city)
      if (city) {
        parts.push(city)
      }

      // Department
      const department = safeStringTrim(data.department)
      if (department) {
        parts.push(department)
      }

      // Postal code
      const postalCode = safeStringTrim(data.postalCode)
      if (postalCode) {
        parts.push(postalCode)
      }

      return parts.filter(Boolean).join(', ')
    },

    // Improved parser for Colombian addresses
    parser: (legacyData: any) => {
      // Try to parse Colombian street format from legacy street field
      const street = safeStringTrim(legacyData.street)
      let addressType = 'Calle'
      let primaryStreetNumber = ''
      let secondaryNumber = ''
      let bis = false
      let orientation = ''

      if (street) {
        // Try to extract Colombian address format
        // Examples: "Carrera 15 # 93-07", "Calle 26 bis - 47 este"
        const colombianPattern =
          /(Carrera|Calle|Avenida|Diagonal|Transversal|Circular|Autopista)\s+([0-9A-Za-z]+)\s*(bis)?\s*[-#]\s*([0-9A-Za-z]+)\s*(norte|sur|este|oeste)?/i
        const match = street.match(colombianPattern)

        if (match) {
          addressType = match[1]
          primaryStreetNumber = match[2]
          bis = !!match[3]
          secondaryNumber = match[4]
          orientation = match[5] || ''
        } else {
          // Fallback: just use the street as is
          primaryStreetNumber = street
        }
      }

      return {
        addressType,
        primaryStreetNumber,
        bis,
        secondaryNumber,
        orientation,
        additionalInfo: '',
        neighborhood: safeStringTrim(legacyData.neighborhood),
        city: safeStringTrim(legacyData.city),
        department: safeStringTrim(legacyData.state),
        postalCode: safeStringTrim(legacyData.postalCode)
      }
    },

    validator: (data: ColombianAddress) => {
      const errors: string[] = []

      // Required fields
      const city = safeStringTrim(data.city)
      if (!city) {
        errors.push('Ciudad es requerida')
      }

      const department = safeStringTrim(data.department)
      if (!department) {
        errors.push('Departamento es requerido')
      }

      // Optional validations
      const postalCode = safeStringTrim(data.postalCode)
      if (postalCode && !/^\d{6}$/.test(postalCode)) {
        errors.push('Código postal debe tener 6 dígitos')
      }

      return { valid: errors.length === 0, errors }
    },

    searchableFields: [
      { key: 'neighborhood', label: 'Barrio', mongoPath: 'address.data.neighborhood' },
      { key: 'city', label: 'Ciudad', mongoPath: 'address.data.city' },
      { key: 'department', label: 'Departamento', mongoPath: 'address.data.department' }
    ],

    excelMapping: {
      addressType: {
        label: 'Tipo Vía',
        dbField: 'address.data.addressType'
      },
      primaryStreetNumber: {
        label: 'Número Principal',
        dbField: 'address.data.primaryStreetNumber'
      },
      bis: {
        label: 'Bis',
        dbField: 'address.data.bis'
      },
      secondaryNumber: {
        label: 'Número Secundario',
        dbField: 'address.data.secondaryNumber'
      },
      orientation: {
        label: 'Orientación',
        dbField: 'address.data.orientation'
      },
      additionalInfo: {
        label: 'Info Adicional',
        dbField: 'address.data.additionalInfo'
      },
      neighborhood: {
        label: 'Barrio',
        dbField: 'address.data.neighborhood'
      },
      city: {
        label: 'Ciudad',
        dbField: 'address.data.city'
      },
      department: {
        label: 'Departamento',
        dbField: 'address.data.department'
      },
      postalCode: {
        label: 'Código Postal',
        dbField: 'address.data.postalCode'
      }
    }
  }
}
